import { useEffect, useState, useRef, useMemo } from "react";
import { Search, Filter, RotateCcw, ChevronDown, Map as MapIcon, X } from "lucide-react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useSpecialists } from "@/api/hooks/specialists.hooks";
import { useSpecialties } from "@/api/hooks/specialties.hooks";
import { Combobox } from "@/components/ui/Combobox";
import { ZipInput } from "@/components/ui/ZipInput";
import { FilterSelect } from "@/components/ui/FilterSelect";
import Button from "@/components/ui/Button";
import { SpecialistMap, type MapSpecialist } from "@/components/SpecialistMap";
import { VisitType } from "@/api/enums";
import {
  getSpecialistPrice,
  locationMapKey,
} from "@/utils/specialist";
import {
  SpecialistCard,
  SpecialistCardSkeleton,
  SEARCH_FILTERS,
  INITIAL_SEARCH_FILTERS,
  INPUT_WHITE,
} from "@/features/search";
import { STORAGE_KEYS } from "@/constants/storage";

const APP_HEADER_HEIGHT_PX = 88;

export function SearchPage() {
  const navigate = useNavigate();
  const { specialty: specialtyParam = "", zipCode = "" } = useSearch({ from: "/search" });
  const { data: specialties = [] } = useSpecialties();
  const [specialty, setSpecialty] = useState(specialtyParam);
  const [zip, setZip] = useState(zipCode);
  const [filters, setFilters] = useState(INITIAL_SEARCH_FILTERS);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedLocationKey, setSelectedLocationKey] = useState<string | null>(
    null,
  );
  const [hoveredLocationKey, setHoveredLocationKey] = useState<string | null>(
    null,
  );
  const [mobileMapOpen, setMobileMapOpen] = useState(false);
  const searchHeaderRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const header = searchHeaderRef.current;
    if (!header) return;
    const check = () => {
      const top = header.getBoundingClientRect().top;
      if (top <= APP_HEADER_HEIGHT_PX + 20) setIsExpanded(false);
    };
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!searchHeaderRef.current?.contains(e.target as Node)) setIsExpanded(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("[data-keep-selection]"))
        setSelectedLocationKey(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setSpecialty(specialtyParam);
    setZip(zipCode);
    if (/^\d{5}$/.test(zipCode.trim())) {
      sessionStorage.setItem(STORAGE_KEYS.SEARCHED_ZIP_CODE, zipCode.trim());
    }
  }, [specialtyParam, zipCode]);

  const urlHasValidSearch = !!specialtyParam && /^\d{5}$/.test(zipCode.trim());
  const params = urlHasValidSearch
    ? {
        specialty: specialtyParam,
        zipCode: zipCode.trim(),
        visitType:
          filters.visit === "all" ? undefined : (Number(filters.visit) as VisitType),
        priceRange:
          filters.price === "all" ? undefined : Number(filters.price),
      }
    : null;

  const {
    data: specialistsPages,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useSpecialists(params);
  const specialists = useMemo(
    () => specialistsPages?.pages.flatMap((p) => p.data) ?? [],
    [specialistsPages],
  );
  const total = specialistsPages?.pages[0]?.pagination.total ?? 0;
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const options = specialties.map((s) => ({
    value: s.slug,
    label: s.name,
  }));

  const formHasValidInput = !!specialty && /^\d{5}$/.test(zip.trim());
  const formIsDirty = specialty !== specialtyParam || zip.trim() !== zipCode;
  const canSubmitNewSearch = formHasValidInput && formIsDirty;

  const specialtyLabel = urlHasValidSearch
    ? (specialties.find((s) => s.slug === specialtyParam)?.name ?? "Specialists")
    : "";
  const locationLabel = urlHasValidSearch ? zipCode : "";

  const rows = useMemo(
    () =>
      specialists.map((specialist, i) => ({
        specialist,
        rowKey: `row-${i}`,
      })),
    [specialists],
  );

  // One marker per physical location (a doctor with several offices gets
  // several pins), plus a lookup back to the card that owns each marker.
  const { mapSpecialists, rowKeyByMapKey } = useMemo(() => {
    const markers: MapSpecialist[] = [];
    const rowKeyByMapKey = new Map<string, string>();
    for (const { specialist, rowKey } of rows) {
      const initials =
        `${specialist.specialistFirstName?.[0] ?? ""}${specialist.specialistLastName?.[0] ?? ""}`.toUpperCase();
      const name = `${specialist.specialistTitle ?? ""} ${specialist.specialistFirstName ?? ""} ${specialist.specialistLastName ?? ""} ${specialist.specialistSuffix ?? ""}`
        .replace(/\s+/g, " ")
        .trim();
      for (const practice of specialist.practices) {
        if (!practice.practiceLocationId) continue;
        if (practice.AvailableSlots.length === 0) continue;
        const mapKey = locationMapKey(
          specialist.specialistId,
          practice.practiceLocationId,
        );
        const [specialistSlug, practiceSlug] = practice.slug.split("/");
        rowKeyByMapKey.set(mapKey, rowKey);
        markers.push({
          id: mapKey,
          name,
          lat: practice.practiceLatitude,
          lng: practice.practiceLongitude,
          specialty: practice.specialties,
          price: getSpecialistPrice(specialist),
          address: [practice.practiceCity, practice.practiceState]
            .filter(Boolean)
            .join(", "),
          profilePictureUrl: specialist.specialistProfilePicture ?? undefined,
          initials: initials || undefined,
          profileHref:
            specialistSlug && practiceSlug
              ? `/specialist-profile/${specialistSlug}/${practiceSlug}`
              : undefined,
        });
      }
    }
    return { mapSpecialists: markers, rowKeyByMapKey };
  }, [rows]);

  const showMap = mapSpecialists.length > 0;
  const selectedRowKey = selectedLocationKey
    ? (rowKeyByMapKey.get(selectedLocationKey) ?? null)
    : null;

  const handleMarkerClick = (mapKey: string) => {
    setSelectedLocationKey(mapKey);
    setMobileMapOpen(false);
    const rowKey = rowKeyByMapKey.get(mapKey);
    if (!rowKey) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(`specialist-card-${rowKey}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <header
        ref={searchHeaderRef}
        className="sticky top-20 md:top-24 z-40 rounded-b-xl bg-gradient-to-r from-[color-mix(in_oklab,var(--color-secondary)_80%,transparent)] to-[var(--color-cyan)] shadow-lg"
      >
        <div className="container">
          <button
            type="button"
            onClick={() => setIsExpanded((e) => !e)}
            className="flex w-full items-center justify-between gap-4 py-3.5 text-left transition-colors hover:bg-white/5"
            aria-label={isExpanded ? "Collapse filters" : "Expand filters"}
          >
            <div className="flex min-w-0 items-center gap-2">
              <Search className="h-4 w-4 shrink-0 text-white/90" />
              <span className="truncate text-base text-white/90">
                {urlHasValidSearch ? (
                  <>
                    {specialtyLabel} near {locationLabel}
                    {!isLoading && ` • ${total} Specialists Found`}
                  </>
                ) : (
                  "Search Results"
                )}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Filter className="h-4 w-4 text-white/70" />
              <span className="text-base text-white/90">Filters</span>
              <ChevronDown
                className={`h-4 w-4 text-white/70 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
              />
            </div>
          </button>

          <div
            className={`transition-[max-height] duration-300 ease-out ${isExpanded ? "overflow-visible" : "overflow-hidden"}`}
            style={{ maxHeight: isExpanded ? 480 : 0 }}
          >
            <div className="space-y-6 pb-6 pt-2">
              <form
                className="flex flex-col lg:flex-row gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (canSubmitNewSearch)
                    navigate({
                      to: "/search",
                      search: { specialty, zipCode: zip.trim() },
                    });
                }}
              >
                <div className="flex flex-col sm:flex-row gap-2 flex-1">
                  <div className={`relative flex-1 ${INPUT_WHITE}`}>
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                    <div className="[&_input]:pl-10">
                      <Combobox
                        value={specialty}
                        onValueChange={setSpecialty}
                        options={options}
                        placeholder="Search specialty"
                      />
                    </div>
                  </div>
                  <div className={INPUT_WHITE}>
                    <ZipInput
                      value={zip}
                      setValue={setZip}
                      inputId="zip"
                      labelId="zip-label"
                    />
                  </div>
                  <Button
                    variant="outline"
                    type="submit"
                    disabled={!canSubmitNewSearch}
                  >
                    <Search className="w-4 h-4" /> New Search
                  </Button>
                </div>
              </form>

              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2 text-white/90 text-base">
                  <Filter className="w-4 h-4" />
                  <span>Filters:</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {SEARCH_FILTERS.map(({ id, options, placeholder }) => (
                    <FilterSelect
                      key={id}
                      value={filters[id]}
                      onValueChange={(val) =>
                        setFilters((f) => ({ ...f, [id]: val }))
                      }
                      options={[...options]}
                      placeholder={placeholder}
                      className="bg-white/90 border-white/20 text-sm"
                    />
                  ))}
                  <Button
                    variant="outline"
                    onClick={() => setFilters(INITIAL_SEARCH_FILTERS)}
                  >
                    <RotateCcw className="w-4 h-4" /> Reset All
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container py-8">
        {!urlHasValidSearch ? (
          <p className="body-copy text-muted-foreground text-center py-12">
            Enter a specialty and valid ZIP code to search.
          </p>
        ) : (
          <div
            className={
              showMap
                ? "lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(320px,480px)] lg:gap-6"
                : "max-w-4xl mx-auto"
            }
          >
            <div className="space-y-6">
              {isLoading ? (
                <div
                  className="space-y-6"
                  role="status"
                  aria-label="Loading specialists"
                >
                  {Array.from({ length: 3 }, (_, i) => (
                    <SpecialistCardSkeleton key={i} delay={i * 80} />
                  ))}
                </div>
              ) : specialists.length === 0 ? (
                <p className="body-copy text-muted-foreground text-center py-12">
                  No specialists found. Try adjusting your filters.
                </p>
              ) : (
                <div className="space-y-6 animate-[skeleton-fade-in_0.25s_ease-out]">
                  {rows.map(({ specialist, rowKey }) => (
                    <div
                      key={rowKey}
                      id={`specialist-card-${rowKey}`}
                      data-keep-selection
                      className={`scroll-mt-40 rounded-xl transition-shadow duration-300 ${
                        selectedRowKey === rowKey
                          ? "ring-2 ring-secondary ring-offset-2 ring-offset-background"
                          : ""
                      }`}
                    >
                      <SpecialistCard
                        specialist={specialist}
                        onLocationHover={setHoveredLocationKey}
                      />
                    </div>
                  ))}
                  {hasNextPage && (
                    <>
                      <div ref={sentinelRef} />
                      {isFetchingNextPage &&
                        Array.from({ length: 2 }, (_, i) => (
                          <SpecialistCardSkeleton key={`next-${i}`} delay={i * 80} />
                        ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {showMap && (
              <aside
                data-keep-selection
                className={
                  mobileMapOpen
                    ? "fixed inset-0 z-50 bg-background flex flex-col lg:static lg:bg-transparent"
                    : "hidden lg:block"
                }
              >
                {mobileMapOpen && (
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border lg:hidden">
                    <span className="text-sm font-medium">{total} on map</span>
                    <button
                      type="button"
                      onClick={() => setMobileMapOpen(false)}
                      className="rounded-full p-1.5 hover:bg-muted transition-colors"
                      aria-label="Close map"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                )}
                <div
                  className={
                    mobileMapOpen
                      ? "flex-1 lg:sticky lg:top-44 lg:h-[calc(100vh-12rem)] lg:rounded-xl lg:overflow-hidden lg:border lg:border-border lg:shadow-sm"
                      : "sticky top-44 h-[calc(100vh-12rem)] rounded-xl overflow-hidden border border-border shadow-sm"
                  }
                >
                  <SpecialistMap
                    specialists={mapSpecialists}
                    selectedId={selectedLocationKey}
                    hoveredId={hoveredLocationKey}
                    onMarkerClick={handleMarkerClick}
                    onMarkerHover={setHoveredLocationKey}
                    className="w-full h-full"
                  />
                </div>
              </aside>
            )}
          </div>
        )}
      </main>

      {urlHasValidSearch && showMap && !mobileMapOpen && (
        <button
          type="button"
          onClick={() => setMobileMapOpen(true)}
          className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-white shadow-xl hover:bg-primary/90 transition-colors"
        >
          <MapIcon className="h-4 w-4" />
          Show map
        </button>
      )}
    </div>
  );
}
